import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Assignment,
  AssignmentReturn,
  Autorenew,
  Build,
  CheckCircle,
  CloudUpload,
  Delete,
  Edit,
  FlagOutlined,
  InfoOutlined,
  KeyboardReturn,
  Person,
  PictureAsPdf,
  Search,
  Warning,
} from "@mui/icons-material";
import { PpeSlipDialog } from "./PpeSlipDialog";
import { PpeReturnSlipDialog } from "./PpeReturnSlipDialog";
import {
  useGetSheqPpeRecordsQuery,
  useGetSheqWorkerHoldingsQuery,
  useIssueSheqPpeMutation,
  useUpdateSheqPpeRecordMutation,
  useReturnSheqPpeMutation,
  useDeleteSheqPpeRecordMutation,
  useGetSheqCatalogueQuery,
  SheqCatalogueItem,
  SheqPpeRecord,
  PpeStatus,
  PpeCondition,
} from "../../../redux/sheqApi";
import { LabourWorker } from "../../../redux/labourTrackerApi";
import { useGetComsBlocksQuery } from "../../../redux/comsApi";
import { useGetLabourWorkersQuery } from "../../../redux/labourTrackerApi";

const NAVY = "#123456";

/** Safely parse availableSizes – handles JSON arrays, comma-separated strings, or single values */
function parseSizes(raw: string): string[] {
  if (!raw) return [];
  const trimmed = raw.trim();
  if (trimmed.startsWith("[")) {
    try {
      return JSON.parse(trimmed);
    } catch {
      /* fall through */
    }
  }
  // Comma-separated or single value
  return trimmed
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

const STATUS_COLOR: Record<PpeStatus, string> = {
  ACTIVE: "#2e7d32",
  RETURNED: "#1565c0",
  LOST: "#e65100",
  DAMAGED: "#c62828",
  EXPIRED: "#6d4c41",
};

const CONDITION_COLOR: Record<PpeCondition, string> = {
  NEW: "#2e7d32",
  GOOD: "#388e3c",
  FAIR: "#f57c00",
  POOR: "#c62828",
  CONDEMNED: "#4a148c",
};

const CONDITIONS: PpeCondition[] = ["NEW", "GOOD", "FAIR", "POOR", "CONDEMNED"];
const STATUSES: PpeStatus[] = [
  "ACTIVE",
  "RETURNED",
  "LOST",
  "DAMAGED",
  "EXPIRED",
];

function daysUntil(dateStr?: string): number {
  if (!dateStr) return 9999;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${dateStr}T00:00:00`);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// ─── Missing PPE alert ────────────────────────────────────────────────────
const MissingPpeAlert: React.FC<{
  catalogue: SheqCatalogueItem[];
  holdings: SheqPpeRecord[];
}> = ({ catalogue, holdings }) => {
  const heldIds = useMemo(
    () => new Set(holdings.map((h) => h.ppeItemId)),
    [holdings],
  );
  const missing = useMemo(
    () =>
      catalogue.filter(
        (c) =>
          c.isActive &&
          c.isStandardIssue === true &&
          !heldIds.has(c.id!),
      ),
    [catalogue, heldIds],
  );

  if (missing.length === 0) return null;

  return (
    <Alert severity="warning" icon={<Warning />}>
      <Typography variant="body2" fontWeight={700} mb={0.5}>
        Missing standard-issue PPE — {missing.length} item
        {missing.length !== 1 ? "s" : ""} not currently issued
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
        {missing.map((item) => (
          <Chip
            key={item.id}
            label={`[${item.code}] ${item.name}`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontSize: 10, height: 20 }}
          />
        ))}
      </Box>
    </Alert>
  );
};

// ─── Issue / Edit dialog ───────────────────────────────────────────────────
interface IssueDialogProps {
  open: boolean;
  record: SheqPpeRecord | null;
  onClose: () => void;
  prefilledWorkerId?: string;
  prefilledPpeItemId?: string;
}

/** Compact chip for a single active holding */
const HoldingChip: React.FC<{ r: SheqPpeRecord }> = ({ r }) => {
  const days = daysUntil(r.replacementDueDate);
  const expired = days < 0;
  const expiring = !expired && days <= 30;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        border: `1px solid ${
          expired ? "#c62828" : expiring ? "#f57c00" : "#c8e6c9"
        }`,
        borderRadius: 1,
        px: 1,
        py: 0.4,
        bgcolor: expired ? "#fff8f8" : expiring ? "#fffde7" : "#f1f8e9",
      }}
    >
      {r.ppeCode && (
        <Typography
          component="span"
          sx={{
            bgcolor: NAVY,
            color: "#fff",
            px: 0.5,
            borderRadius: 0.5,
            fontSize: 10,
            fontWeight: 700,
          }}
        >
          {r.ppeCode}
        </Typography>
      )}
      <Typography
        variant="caption"
        fontWeight={600}
        noWrap
        sx={{ maxWidth: 120 }}
      >
        {r.ppeName}
      </Typography>
      {r.size && (
        <Typography variant="caption" color="text.secondary">
          ({r.size})
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{
          color: expired ? "#c62828" : expiring ? "#f57c00" : "#2e7d32",
          fontWeight: 600,
        }}
      >
        {expired ? "EXPIRED" : expiring ? `${days}d left` : `✓`}
      </Typography>
    </Box>
  );
};

const IssueDialog: React.FC<IssueDialogProps> = ({ open, record, onClose, prefilledWorkerId, prefilledPpeItemId }) => {
  const { data: catalogue = [] } = useGetSheqCatalogueQuery();
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const { data: workers = [] } = useGetLabourWorkersQuery({ active: true });
  const [issuePpe, { isLoading: issuing }] = useIssueSheqPpeMutation();
  const [updatePpe, { isLoading: updating }] = useUpdateSheqPpeRecordMutation();
  const [returnPpe, { isLoading: returning }] = useReturnSheqPpeMutation();

  const editing = !!record;

  const [workerSearch, setWorkerSearch] = useState("");
  const [form, setForm] = useState<Partial<SheqPpeRecord>>(
    record ?? { issuedDate: today(), quantity: 1, condition: "NEW" },
  );
  // Inline return state for resolving conflict
  const [returnDate, setReturnDate] = useState(today());
  const [returnCondition, setReturnCondition] = useState<PpeCondition>("GOOD");
  const [returnNotes, setReturnNotes] = useState("");
  const [conflictResolved, setConflictResolved] = useState(false);

  React.useEffect(() => {
    if (open) {
      const base = record ?? { issuedDate: today(), quantity: 1, condition: "NEW" as PpeCondition };
      setForm({
        ...base,
        ...(prefilledWorkerId && !record ? { workerId: prefilledWorkerId } : {}),
        ...(prefilledPpeItemId && !record ? { ppeItemId: prefilledPpeItemId } : {}),
      });
      setWorkerSearch("");
      setConflictResolved(false);
      setReturnDate(today());
      setReturnCondition("GOOD");
      setReturnNotes("");
    }
  }, [record, open, prefilledWorkerId, prefilledPpeItemId]);

  // Reset conflict resolved when worker or PPE item changes
  React.useEffect(() => {
    setConflictResolved(false);
  }, [form.workerId, form.ppeItemId]);

  const set = (key: keyof SheqPpeRecord, val: unknown) =>
    setForm((f) => ({ ...f, [key]: val }));

  // Filtered worker list for search
  const filteredWorkers = useMemo(() => {
    const q = workerSearch.toLowerCase();
    if (!q) return workers;
    return workers.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.nationalId ?? "").toLowerCase().includes(q) ||
        (w.firstName ?? "").toLowerCase().includes(q) ||
        (w.surname ?? "").toLowerCase().includes(q),
    );
  }, [workers, workerSearch]);

  // Worker holdings (active PPE)
  const { data: holdings = [], isFetching: holdingsFetching } =
    useGetSheqWorkerHoldingsQuery(form.workerId!, {
      skip: !form.workerId || editing,
    });

  // Conflict: selected PPE item is already active for this worker
  const conflictRecord = useMemo(
    () =>
      !editing && form.workerId && form.ppeItemId
        ? holdings.find((h) => h.ppeItemId === form.ppeItemId) ?? null
        : null,
    [holdings, form.workerId, form.ppeItemId, editing],
  );

  const selectedCat = catalogue.find((c) => c.id === form.ppeItemId);
  const sizes: string[] = selectedCat?.availableSizes
    ? parseSizes(selectedCat.availableSizes)
    : [];

  // Inline return of conflicting record
  const handleReturnConflict = async () => {
    if (!conflictRecord?.id) return;
    try {
      await returnPpe({
        id: conflictRecord.id,
        returnDate,
        returnCondition,
        returnNotes,
      }).unwrap();
      setConflictResolved(true);
    } catch {
      // handled
    }
  };

  const handleSave = async () => {
    if (!form.workerId || !form.ppeItemId || !form.issuedDate) return;
    const worker = workers.find((w) => w.id === form.workerId);
    const cat = catalogue.find((c) => c.id === form.ppeItemId);
    const block = blocks.find((b) => b.id === form.blockId);
    const replacementDueDate = cat
      ? addDays(form.issuedDate!, cat.replacementCycleDays)
      : addDays(form.issuedDate!, 365);

    const payload = {
      workerId: form.workerId!,
      workerName: worker?.name ?? form.workerName ?? "",
      workerEmployeeNo: worker?.nationalId ?? form.workerEmployeeNo,
      blockId: form.blockId,
      blockName: block?.name,
      ppeItemId: form.ppeItemId!,
      ppeName: cat?.name ?? form.ppeName ?? "",
      ppeCode: cat?.code,
      size: form.size,
      quantity: form.quantity ?? 1,
      batchOrSerialNumber: form.batchOrSerialNumber,
      issuedDate: form.issuedDate!,
      replacementDueDate,
      status: "ACTIVE" as PpeStatus,
      condition: (form.condition ?? "NEW") as PpeCondition,
      notes: form.notes,
    };

    try {
      if (editing && record?.id) {
        await updatePpe({ id: record.id, body: payload }).unwrap();
      } else {
        await issuePpe({
          ...payload,
          overrideExisting: conflictResolved,
        }).unwrap();
      }
      onClose();
    } catch {
      // handled
    }
  };

  const saving = issuing || updating;
  const blocked = !editing && !!conflictRecord && !conflictResolved;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? "Edit PPE Record" : "Issue PPE"}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {/* ── Worker search ─────────────────────────────────────── */}
          {!editing && (
            <TextField
              label="Search worker by name or ID"
              size="small"
              value={workerSearch}
              onChange={(e) => setWorkerSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: "#999" }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          <FormControl fullWidth size="small" required>
            <InputLabel>Worker</InputLabel>
            <Select
              label="Worker"
              value={form.workerId ?? ""}
              onChange={(e) => set("workerId", e.target.value)}
            >
              {filteredWorkers.length === 0 && (
                <MenuItem disabled>No workers match search</MenuItem>
              )}
              {filteredWorkers.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  <Box>
                    <Typography variant="body2">{w.name}</Typography>
                    {w.nationalId && (
                      <Typography variant="caption" color="text.secondary">
                        {w.nationalId} · {w.role}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ── Holdings at a glance ──────────────────────────────── */}
          {!editing && form.workerId && (
            <Box
              sx={{
                bgcolor: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                p: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Person sx={{ fontSize: 16, color: NAVY }} />
                <Typography variant="caption" fontWeight={700} color={NAVY}>
                  CURRENT PPE HOLDINGS
                </Typography>
                {holdingsFetching && <CircularProgress size={12} />}
              </Box>
              {holdings.length === 0 && !holdingsFetching ? (
                <Typography variant="caption" color="text.secondary">
                  No active PPE issued to this worker.
                </Typography>
              ) : (
                <Box display="flex" flexWrap="wrap" gap={0.75}>
                  {holdings.map((h) => (
                    <HoldingChip key={h.id} r={h} />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── Missing required PPE ─────────────────────────────────── */}
          {!editing && form.workerId && !holdingsFetching && (
            <MissingPpeAlert catalogue={catalogue} holdings={holdings} />
          )}

          <Divider />

          {/* ── Site ─────────────────────────────────────────────── */}
          <FormControl fullWidth size="small">
            <InputLabel>Site / Block</InputLabel>
            <Select
              label="Site / Block"
              value={form.blockId ?? ""}
              onChange={(e) => set("blockId", e.target.value)}
            >
              <MenuItem value="">— None —</MenuItem>
              {blocks.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ── PPE item ──────────────────────────────────────────── */}
          <FormControl fullWidth size="small" required>
            <InputLabel>PPE Item</InputLabel>
            <Select
              label="PPE Item"
              value={form.ppeItemId ?? ""}
              onChange={(e) => set("ppeItemId", e.target.value)}
            >
              {catalogue
                .filter((c) => c.isActive)
                .map((c) => {
                  const alreadyHeld = holdings.some(
                    (h) => h.ppeItemId === c.id,
                  );
                  return (
                    <MenuItem key={c.id} value={c.id}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        width="100%"
                      >
                        <Typography variant="body2" flex={1}>
                          [{c.code}] {c.name}
                        </Typography>
                        {alreadyHeld && !editing && (
                          <Chip
                            label="Already issued"
                            size="small"
                            color="warning"
                            sx={{ fontSize: 10, height: 18 }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>

          {/* ── Duplicate conflict warning ───────────────────────── */}
          {conflictRecord && !conflictResolved && (
            <Alert
              severity="error"
              icon={<Warning />}
              sx={{ "& .MuiAlert-message": { width: "100%" } }}
            >
              <Typography variant="body2" fontWeight={700} mb={0.5}>
                Already issued — action required before issuing new PPE
              </Typography>
              <Typography variant="caption" display="block" mb={1}>
                <b>{conflictRecord.ppeName}</b>
                {conflictRecord.size ? ` (${conflictRecord.size})` : ""} was
                issued on <b>{conflictRecord.issuedDate}</b> · condition:{" "}
                <b>{conflictRecord.condition}</b> · due:{" "}
                <b>{conflictRecord.replacementDueDate}</b>
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1.5}
              >
                Inspect the existing item and record its return before issuing a
                replacement.
              </Typography>

              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" gap={1.5} flexWrap="wrap">
                  <TextField
                    label="Return date"
                    type="date"
                    size="small"
                    value={returnDate}
                    onChange={(e) => setReturnDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: "1 1 130px", bgcolor: "#fff" }}
                  />
                  <FormControl
                    size="small"
                    sx={{ flex: "1 1 150px", bgcolor: "#fff" }}
                  >
                    <InputLabel>Condition at return</InputLabel>
                    <Select
                      label="Condition at return"
                      value={returnCondition}
                      onChange={(e) =>
                        setReturnCondition(e.target.value as PpeCondition)
                      }
                    >
                      {CONDITIONS.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <TextField
                  label="Return notes (inspection findings)"
                  size="small"
                  multiline
                  rows={2}
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  sx={{ bgcolor: "#fff" }}
                />
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  onClick={handleReturnConflict}
                  disabled={returning}
                  startIcon={
                    returning ? (
                      <CircularProgress size={14} />
                    ) : (
                      <KeyboardReturn />
                    )
                  }
                  sx={{ alignSelf: "flex-start" }}
                >
                  Return existing &amp; proceed to issue new
                </Button>
              </Box>
            </Alert>
          )}

          {/* ── Conflict resolved confirmation ──────────────────── */}
          {conflictResolved && (
            <Alert severity="success" icon={<CheckCircle />}>
              <Typography variant="body2">
                Existing PPE returned. You can now issue the replacement.
              </Typography>
            </Alert>
          )}

          {/* ── Issue details (hidden while conflict unresolved) ── */}
          <Collapse in={!blocked}>
            <Box display="flex" flexDirection="column" gap={2}>
              {selectedCat?.requiresSize && sizes.length > 0 && (
                <FormControl fullWidth size="small">
                  <InputLabel>Size</InputLabel>
                  <Select
                    label="Size"
                    value={form.size ?? ""}
                    onChange={(e) => set("size", e.target.value)}
                  >
                    {sizes.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box display="flex" gap={2}>
                <TextField
                  label="Quantity"
                  type="number"
                  size="small"
                  value={form.quantity ?? 1}
                  onChange={(e) => set("quantity", Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  sx={{ width: 120 }}
                />
                <TextField
                  label="Issue Date"
                  type="date"
                  size="small"
                  value={form.issuedDate ?? ""}
                  onChange={(e) => set("issuedDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Box>

              <TextField
                label="Batch / Serial Number"
                size="small"
                value={form.batchOrSerialNumber ?? ""}
                onChange={(e) => set("batchOrSerialNumber", e.target.value)}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Condition at Issue</InputLabel>
                <Select
                  label="Condition at Issue"
                  value={form.condition ?? "NEW"}
                  onChange={(e) => set("condition", e.target.value)}
                >
                  {CONDITIONS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Notes"
                size="small"
                multiline
                rows={2}
                value={form.notes ?? ""}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Box>
          </Collapse>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.workerId || !form.ppeItemId || blocked}
          sx={{ bgcolor: NAVY }}
        >
          {saving ? (
            <CircularProgress size={16} />
          ) : editing ? (
            "Save"
          ) : (
            "Issue PPE"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Bulk Issue dialog ────────────────────────────────────────────────────
interface BulkRow {
  employeeNo: string;
  ppeCode: string;
  size: string;
  worker?: LabourWorker;
  catalogItem?: SheqCatalogueItem;
  errors: string[];
}

interface BulkIssueDialogProps {
  open: boolean;
  onClose: () => void;
}

const BulkIssueDialog: React.FC<BulkIssueDialogProps> = ({ open, onClose }) => {
  const { data: catalogue = [] } = useGetSheqCatalogueQuery();
  const { data: workers = [] } = useGetLabourWorkersQuery({ active: true });
  const [issuePpe] = useIssueSheqPpeMutation();

  const [rows, setRows] = useState<BulkRow[]>([]);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<{ issued: number; failed: number } | null>(null);

  React.useEffect(() => {
    if (!open) {
      setRows([]);
      setResult(null);
    }
  }, [open]);

  const parseCSV = (text: string) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return;
    const dataLines = lines.slice(1);
    const parsed: BulkRow[] = dataLines.map((line) => {
      const cols = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      const [employeeNo = "", ppeCode = "", size = ""] = cols;
      const errors: string[] = [];

      const worker = workers.find(
        (w) =>
          (w.nationalId ?? "").toLowerCase() === employeeNo.toLowerCase() ||
          w.name.toLowerCase() === employeeNo.toLowerCase(),
      );
      const catalogItem = catalogue.find(
        (c) => (c.code ?? "").toLowerCase() === ppeCode.toLowerCase(),
      );

      if (!employeeNo) errors.push("Missing employee number");
      else if (!worker) errors.push(`Unknown employee: "${employeeNo}"`);

      if (!ppeCode) errors.push("Missing PPE code");
      else if (!catalogItem) errors.push(`Unknown PPE code: "${ppeCode}"`);

      if (catalogItem?.requiresSize && !size)
        errors.push(`Size required for ${catalogItem.name}`);

      return { employeeNo, ppeCode, size, worker, catalogItem, errors };
    });
    setRows(parsed);
    setResult(null);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => parseCSV((ev.target?.result as string) ?? "");
    reader.readAsText(file);
    e.target.value = "";
  };

  const validRows = rows.filter((r) => r.errors.length === 0);
  const errorCount = rows.length - validRows.length;

  const handleCommit = async () => {
    setCommitting(true);
    const issuedDate = today();
    let issued = 0;
    let failed = 0;
    for (const row of validRows) {
      if (!row.worker?.id || !row.catalogItem?.id) continue;
      const replacementDueDate = addDays(
        issuedDate,
        row.catalogItem.replacementCycleDays,
      );
      try {
        await issuePpe({
          workerId: row.worker.id,
          workerName: row.worker.name,
          workerEmployeeNo: row.worker.nationalId,
          ppeItemId: row.catalogItem.id,
          ppeName: row.catalogItem.name,
          ppeCode: row.catalogItem.code,
          size: row.size || undefined,
          quantity: 1,
          issuedDate,
          replacementDueDate,
          status: "ACTIVE",
          condition: "NEW",
          overrideExisting: false,
        }).unwrap();
        issued++;
      } catch {
        failed++;
      }
    }
    setCommitting(false);
    setResult({ issued, failed });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Issue PPE</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          {/* Instructions */}
          <Alert severity="info">
            <Typography variant="body2" fontWeight={600} mb={0.5}>
              CSV format (with header row):
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
              employee_number,ppe_code,size
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              mt={0.5}
            >
              Use the employee's ID / national ID or full name · PPE codes: WS,
              SS, HH, RT … · Size required for sized items (WS, SS, GB …)
            </Typography>
          </Alert>

          {/* Upload */}
          <Box>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
              sx={{ borderColor: NAVY, color: NAVY }}
            >
              Upload CSV
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleFile}
              />
            </Button>
          </Box>

          {/* Preview table */}
          {rows.length > 0 && !result && (
            <Box>
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <Typography variant="body2" fontWeight={700}>
                  Preview — {rows.length} row{rows.length !== 1 ? "s" : ""}
                </Typography>
                {errorCount > 0 && (
                  <Chip
                    label={`${errorCount} error${errorCount !== 1 ? "s" : ""} (will be skipped)`}
                    size="small"
                    color="error"
                    sx={{ fontSize: 11 }}
                  />
                )}
                {validRows.length > 0 && (
                  <Chip
                    label={`${validRows.length} ready`}
                    size="small"
                    color="success"
                    sx={{ fontSize: 11 }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  maxHeight: 320,
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                      {[
                        "Employee No",
                        "PPE Code",
                        "Size",
                        "Resolved Worker",
                        "Resolved Item",
                        "Status",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "7px 10px",
                            textAlign: "left",
                            fontWeight: 700,
                            fontSize: 11,
                            color: "#555",
                            borderBottom: "1px solid #e0e0e0",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const hasErr = row.errors.length > 0;
                      return (
                        <tr
                          key={i}
                          style={{
                            background: hasErr
                              ? "#fff8f8"
                              : i % 2 === 0
                                ? "#fff"
                                : "#fafafa",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <td style={{ padding: "6px 10px" }}>
                            {row.employeeNo || "—"}
                          </td>
                          <td style={{ padding: "6px 10px" }}>
                            {row.ppeCode || "—"}
                          </td>
                          <td style={{ padding: "6px 10px" }}>
                            {row.size || "—"}
                          </td>
                          <td style={{ padding: "6px 10px" }}>
                            {row.worker ? (
                              <span style={{ color: "#2e7d32" }}>
                                {row.worker.name}
                              </span>
                            ) : (
                              <span style={{ color: "#c62828" }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: "6px 10px" }}>
                            {row.catalogItem ? (
                              <span style={{ color: "#2e7d32" }}>
                                {row.catalogItem.name}
                              </span>
                            ) : (
                              <span style={{ color: "#c62828" }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: "6px 10px" }}>
                            {hasErr ? (
                              <Tooltip title={row.errors.join(" · ")}>
                                <Chip
                                  label="Error"
                                  size="small"
                                  color="error"
                                  sx={{ fontSize: 10, cursor: "help" }}
                                />
                              </Tooltip>
                            ) : (
                              <Chip
                                label="Ready"
                                size="small"
                                color="success"
                                sx={{ fontSize: 10 }}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}

          {committing && <LinearProgress />}

          {result && (
            <Alert severity={result.failed > 0 ? "warning" : "success"}>
              <Typography variant="body2" fontWeight={700}>
                Done — {result.issued} issued, {result.failed} skipped (already
                active or conflict).
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? "Close" : "Cancel"}</Button>
        {!result && validRows.length > 0 && (
          <Button
            variant="contained"
            onClick={handleCommit}
            disabled={committing}
            sx={{ bgcolor: NAVY }}
          >
            {committing ? (
              <CircularProgress size={16} />
            ) : (
              `Issue ${validRows.length} item${
                validRows.length !== 1 ? "s" : ""
              }`
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── View Detail dialog ────────────────────────────────────────────────────
interface ViewDetailDialogProps {
  open: boolean;
  record: SheqPpeRecord | null;
  onClose: () => void;
}

const ViewDetailDialog: React.FC<ViewDetailDialogProps> = ({ open, record, onClose }) => {
  if (!record) return null;
  const days = daysUntil(record.replacementDueDate);
  const isExpired = record.status === "ACTIVE" && days < 0;
  const isExpiring = record.status === "ACTIVE" && !isExpired && days <= 30;

  const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <Box display="flex" gap={1} py={0.5} borderBottom="1px solid #f0f0f0">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption">{value ?? "—"}</Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: NAVY, color: "#fff", py: 1.5 }}>
        PPE Record Detail
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box display="flex" flexDirection="column" gap={0.25} mt={1}>
          <Typography variant="subtitle2" fontWeight={700} color={NAVY} mb={0.5}>Worker</Typography>
          <Row label="Name" value={record.workerName} />
          {record.workerEmployeeNo && <Row label="Employee No" value={record.workerEmployeeNo} />}
          {record.department && <Row label="Department" value={record.department} />}
          {record.blockName && <Row label="Site / Block" value={record.blockName} />}
          <Typography variant="subtitle2" fontWeight={700} color={NAVY} mt={1.5} mb={0.5}>PPE Item</Typography>
          <Row label="Item" value={<><span style={{ background: NAVY, color: '#fff', padding: '1px 5px', borderRadius: 3, fontSize: 10, marginRight: 4 }}>{record.ppeCode}</span>{record.ppeName}</>} />
          {record.size && <Row label="Size" value={record.size} />}
          <Row label="Quantity" value={record.quantity} />
          {record.batchOrSerialNumber && <Row label="Batch / Serial No" value={record.batchOrSerialNumber} />}
          <Typography variant="subtitle2" fontWeight={700} color={NAVY} mt={1.5} mb={0.5}>Issue & Status</Typography>
          <Row label="Issued Date" value={record.issuedDate} />
          <Row label="Replacement Due" value={
            <Box component="span" sx={{ color: isExpired ? '#c62828' : isExpiring ? '#f57c00' : 'inherit', fontWeight: (isExpired || isExpiring) ? 700 : 400 }}>
              {record.replacementDueDate}
              {isExpired ? ' (EXPIRED)' : isExpiring ? ` (${days}d left)` : ''}
            </Box>
          } />
          <Row label="Status" value={
            <Chip label={record.status} size="small" sx={{ bgcolor: STATUS_COLOR[record.status], color: '#fff', fontSize: 11, fontWeight: 600, height: 20 }} />
          } />
          <Row label="Condition" value={
            <Chip label={record.condition} size="small" variant="outlined" sx={{ borderColor: CONDITION_COLOR[record.condition], color: CONDITION_COLOR[record.condition], fontSize: 11, height: 20 }} />
          } />
          {record.issuedByName && <Row label="Issued By" value={record.issuedByName} />}
          {record.notes && <Row label="Notes" value={record.notes} />}
          {record.returnDate && (
            <>
              <Typography variant="subtitle2" fontWeight={700} color={NAVY} mt={1.5} mb={0.5}>Return</Typography>
              <Row label="Return Date" value={record.returnDate} />
              {record.returnCondition && <Row label="Condition at Return" value={record.returnCondition} />}
              {record.returnNotes && <Row label="Return Notes" value={record.returnNotes} />}
            </>
          )}
          {record.staffAcknowledgedAt && <Row label="Acknowledged At" value={new Date(record.staffAcknowledgedAt).toLocaleDateString()} />}
          {record.createdAt && <Row label="Created" value={new Date(record.createdAt).toLocaleDateString()} />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Inspection / Condition Check dialog ───────────────────────────────────
interface InspectionDialogProps {
  open: boolean;
  record: SheqPpeRecord | null;
  onClose: () => void;
}

const InspectionDialog: React.FC<InspectionDialogProps> = ({ open, record, onClose }) => {
  const [updatePpe, { isLoading }] = useUpdateSheqPpeRecordMutation();
  const [condition, setCondition] = useState<PpeCondition>("GOOD");
  const [notes, setNotes] = useState("");
  const [inspDate, setInspDate] = useState(today());

  React.useEffect(() => {
    if (open && record) {
      setCondition(record.condition);
      setNotes("");
      setInspDate(today());
    }
  }, [open, record]);

  const handleSave = async () => {
    if (!record?.id) return;
    await updatePpe({ id: record.id, body: { condition, notes: notes || record.notes } }).unwrap();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Record Inspection</DialogTitle>
      <DialogContent>
        {record && (
          <Typography variant="body2" mb={2}>
            <b>{record.workerName}</b> — {record.ppeName}
            {record.size ? ` (${record.size})` : ""}
          </Typography>
        )}
        <Box display="flex" flexDirection="column" gap={2} pt={0.5}>
          <TextField
            label="Inspection Date"
            type="date"
            size="small"
            value={inspDate}
            onChange={(e) => setInspDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Observed Condition</InputLabel>
            <Select
              label="Observed Condition"
              value={condition}
              onChange={(e) => setCondition(e.target.value as PpeCondition)}
            >
              {CONDITIONS.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Inspection Notes / Findings"
            size="small"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observations, defects noted, corrective action…"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading}
          sx={{ bgcolor: NAVY }}
        >
          {isLoading ? <CircularProgress size={16} /> : "Save Inspection"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Return dialog ─────────────────────────────────────────────────────────
interface ReturnDialogProps {
  open: boolean;
  record: SheqPpeRecord | null;
  onClose: () => void;
}

const ReturnDialog: React.FC<ReturnDialogProps> = ({
  open,
  record,
  onClose,
}) => {
  const [returnPpe, { isLoading }] = useReturnSheqPpeMutation();
  const [returnDate, setReturnDate] = useState(today());
  const [returnCondition, setReturnCondition] = useState<PpeCondition>("GOOD");
  const [returnNotes, setReturnNotes] = useState("");

  React.useEffect(() => {
    if (open) {
      setReturnDate(today());
      setReturnCondition("GOOD");
      setReturnNotes("");
    }
  }, [open]);

  const handleReturn = async () => {
    if (!record?.id) return;
    try {
      await returnPpe({
        id: record.id,
        returnDate,
        returnCondition,
        returnNotes,
      }).unwrap();
      onClose();
    } catch {
      // handled
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Return PPE</DialogTitle>
      <DialogContent>
        {record && (
          <Typography variant="body2" mb={2}>
            <b>{record.workerName}</b> — {record.ppeName}
            {record.size ? ` (${record.size})` : ""}
          </Typography>
        )}
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Return Date"
            type="date"
            size="small"
            value={returnDate}
            onChange={(e) => setReturnDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Condition at Return</InputLabel>
            <Select
              label="Condition at Return"
              value={returnCondition}
              onChange={(e) =>
                setReturnCondition(e.target.value as PpeCondition)
              }
            >
              {CONDITIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Notes"
            size="small"
            multiline
            rows={2}
            value={returnNotes}
            onChange={(e) => setReturnNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleReturn}
          disabled={isLoading}
          sx={{ bgcolor: NAVY }}
        >
          {isLoading ? <CircularProgress size={16} /> : "Confirm Return"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Sort helpers ──────────────────────────────────────────────────────────
type SortField = "workerName" | "issuedDate" | "replacementDueDate";
type SortDir = "asc" | "desc";

// ─── Main register ─────────────────────────────────────────────────────────
interface Props {
  blockId?: string;
}

export const SheqPPERegister: React.FC<Props> = ({ blockId }) => {
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [blockFilter, setBlockFilter] = useState<string>(blockId ?? "");
  const [deptFilter, setDeptFilter] = useState<string>("");
  const [ppeTypeFilter, setPpeTypeFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [sortField, setSortField] = useState<SortField>("issuedDate");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [issueOpen, setIssueOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<SheqPpeRecord | null>(null);
  const [returnRecord, setReturnRecord] = useState<SheqPpeRecord | null>(null);
  const [viewRecord, setViewRecord] = useState<SheqPpeRecord | null>(null);
  const [inspectRecord, setInspectRecord] = useState<SheqPpeRecord | null>(null);
  const [replaceRecord, setReplaceRecord] = useState<SheqPpeRecord | null>(null);
  const [slipRecord, setSlipRecord] = useState<SheqPpeRecord | null>(null);
  const [returnSlipRecord, setReturnSlipRecord] = useState<SheqPpeRecord | null>(null);

  const { data: records = [], isLoading } = useGetSheqPpeRecordsQuery({
    blockId: blockFilter || undefined,
    status: statusFilter || undefined,
    from: dateFrom || undefined,
    to: dateTo || undefined,
  });
  const { data: blocks = [] } = useGetComsBlocksQuery();
  const [deleteRecord] = useDeleteSheqPpeRecordMutation();
  const [updatePpeRecord] = useUpdateSheqPpeRecordMutation();

  // Derived filter options
  const departments = useMemo(() => {
    const depts = new Set(records.map((r) => r.department).filter(Boolean) as string[]);
    return Array.from(depts).sort();
  }, [records]);

  const ppeTypes = useMemo(() => {
    const seen = new Map<string, string>();
    records.forEach((r) => { if (r.ppeItemId && !seen.has(r.ppeItemId)) seen.set(r.ppeItemId, r.ppeName); });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [records]);

  // Client-side filter
  const filtered = useMemo(() => {
    let result = records;
    if (deptFilter) result = result.filter((r) => r.department === deptFilter);
    if (ppeTypeFilter) result = result.filter((r) => r.ppeItemId === ppeTypeFilter);
    return result;
  }, [records, deptFilter, ppeTypeFilter]);

  // Sort
  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (field === sortField) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this PPE record?")) return;
    await deleteRecord(id);
  };

  const handleFlagDamaged = async (r: SheqPpeRecord) => {
    if (!window.confirm(`Flag "${r.ppeName}" for ${r.workerName} as DAMAGED?`)) return;
    await updatePpeRecord({ id: r.id!, body: { status: "DAMAGED" } });
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" fontWeight={700}>
          PPE Register
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => setBulkOpen(true)}
            sx={{ borderColor: NAVY, color: NAVY }}
          >
            Bulk Issue
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditRecord(null);
              setIssueOpen(true);
            }}
            sx={{ bgcolor: NAVY }}
          >
            Issue PPE
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Grid container spacing={1.5} mb={2} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All statuses</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {!blockId && (
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Site / Block</InputLabel>
              <Select
                label="Site / Block"
                value={blockFilter}
                onChange={(e) => setBlockFilter(e.target.value)}
              >
                <MenuItem value="">All sites</MenuItem>
                {blocks.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Department</InputLabel>
            <Select
              label="Department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <MenuItem value="">All departments</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>PPE Type</InputLabel>
            <Select
              label="PPE Type"
              value={ppeTypeFilter}
              onChange={(e) => setPpeTypeFilter(e.target.value)}
            >
              <MenuItem value="">All types</MenuItem>
              {ppeTypes.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Issued from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Issued to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        {(deptFilter || ppeTypeFilter || dateFrom || dateTo || statusFilter || (blockFilter && !blockId)) && (
          <Grid item xs={12} sm="auto">
            <Button
              size="small"
              onClick={() => {
                setStatusFilter(""); setBlockFilter(blockId ?? "");
                setDeptFilter(""); setPpeTypeFilter("");
                setDateFrom(""); setDateTo("");
              }}
            >
              Clear filters
            </Button>
          </Grid>
        )}
      </Grid>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : sorted.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Assignment sx={{ fontSize: 56, color: "#ccc", mb: 2 }} />
          <Typography color="text.secondary">
            {records.length === 0 ? "No PPE records found." : "No records match the current filters."}
          </Typography>
        </Box>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>
                    <TableSortLabel
                      active={sortField === "workerName"}
                      direction={sortField === "workerName" ? sortDir : "asc"}
                      onClick={() => handleSort("workerName")}
                    >
                      Staff Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>PPE Item</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>Dept / Block</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>
                    <TableSortLabel
                      active={sortField === "issuedDate"}
                      direction={sortField === "issuedDate" ? sortDir : "asc"}
                      onClick={() => handleSort("issuedDate")}
                    >
                      Issued Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap" }}>
                    <TableSortLabel
                      active={sortField === "replacementDueDate"}
                      direction={sortField === "replacementDueDate" ? sortDir : "asc"}
                      onClick={() => handleSort("replacementDueDate")}
                    >
                      Due Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Condition</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((r) => {
                  const days = daysUntil(r.replacementDueDate);
                  const isExpiring = r.status === "ACTIVE" && days >= 0 && days <= 30;
                  const isExpired = r.status === "ACTIVE" && days < 0;
                  return (
                    <TableRow
                      key={r.id}
                      sx={{
                        bgcolor: isExpired ? "#fff8f8" : isExpiring ? "#fffde7" : undefined,
                        "&:hover": { bgcolor: isExpired ? "#ffeaea" : isExpiring ? "#fff9c4" : "#f9f9f9" },
                      }}
                    >
                      <TableCell sx={{ fontSize: 13 }}>
                        <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                          {r.workerName}
                        </Typography>
                        {r.workerEmployeeNo && (
                          <Typography variant="caption" color="text.secondary">
                            {r.workerEmployeeNo}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {r.ppeCode && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              bgcolor: NAVY, color: "#fff", px: 0.6, py: 0.2,
                              borderRadius: 0.5, mr: 0.5, fontSize: 10,
                            }}
                          >
                            {r.ppeCode}
                          </Typography>
                        )}
                        {r.ppeName}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{r.size ?? "—"}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        <Stack spacing={0}>
                          {r.department && <Typography variant="caption" fontWeight={600}>{r.department}</Typography>}
                          <Typography variant="caption" color="text.secondary">{r.blockName ?? "—"}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{r.issuedDate}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {isExpired ? (
                          <Box display="flex" alignItems="center" gap={0.5} color="#c62828">
                            <Warning sx={{ fontSize: 14 }} />
                            <span>{r.replacementDueDate}</span>
                          </Box>
                        ) : isExpiring ? (
                          <Box display="flex" alignItems="center" gap={0.5} color="#f57c00">
                            <Warning sx={{ fontSize: 14 }} />
                            <span>{r.replacementDueDate}</span>
                          </Box>
                        ) : (
                          r.replacementDueDate
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          size="small"
                          sx={{ bgcolor: STATUS_COLOR[r.status], color: "#fff", fontSize: 11, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.condition}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: CONDITION_COLOR[r.condition], color: CONDITION_COLOR[r.condition], fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.25}>
                          <Tooltip title="Acknowledgement slip (PDF)">
                            <IconButton size="small" sx={{ color: "#6a1b9a" }} onClick={() => setSlipRecord(r)}>
                              <PictureAsPdf sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {(r.status === "RETURNED" || r.status === "DAMAGED" || r.status === "LOST") && (
                            <Tooltip title="Return slip (PDF)">
                              <IconButton size="small" sx={{ color: "#1565c0" }} onClick={() => setReturnSlipRecord(r)}>
                                <AssignmentReturn sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="View detail">
                            <IconButton size="small" onClick={() => setViewRecord(r)}>
                              <InfoOutlined sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Record inspection">
                            <IconButton size="small" onClick={() => setInspectRecord(r)}>
                              <Build sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {r.status === "ACTIVE" && (
                            <Tooltip title="Flag damaged">
                              <IconButton size="small" sx={{ color: "#e65100" }} onClick={() => handleFlagDamaged(r)}>
                                <FlagOutlined sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {r.status === "ACTIVE" && (
                            <Tooltip title="Replace (return & re-issue)">
                              <IconButton
                                size="small"
                                sx={{ color: "#1565c0" }}
                                onClick={() => {
                                  setReplaceRecord(r);
                                  setEditRecord(null);
                                  setIssueOpen(true);
                                }}
                              >
                                <Autorenew sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditRecord(r); setIssueOpen(true); }}>
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {r.status === "ACTIVE" && (
                            <Tooltip title="Return">
                              <IconButton size="small" onClick={() => setReturnRecord(r)}>
                                <KeyboardReturn sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(r.id!)}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <IssueDialog
        open={issueOpen}
        record={editRecord}
        onClose={() => {
          setIssueOpen(false);
          setEditRecord(null);
          setReplaceRecord(null);
        }}
        prefilledWorkerId={replaceRecord?.workerId}
        prefilledPpeItemId={replaceRecord?.ppeItemId}
      />
      <ReturnDialog
        open={!!returnRecord}
        record={returnRecord}
        onClose={() => setReturnRecord(null)}
      />
      <ViewDetailDialog
        open={!!viewRecord}
        record={viewRecord}
        onClose={() => setViewRecord(null)}
      />
      <InspectionDialog
        open={!!inspectRecord}
        record={inspectRecord}
        onClose={() => setInspectRecord(null)}
      />
      <BulkIssueDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
      <PpeSlipDialog
        open={!!slipRecord}
        record={slipRecord}
        onClose={() => setSlipRecord(null)}
      />
      <PpeReturnSlipDialog
        open={!!returnSlipRecord}
        record={returnSlipRecord}
        onClose={() => setReturnSlipRecord(null)}
      />
    </Box>
  );
};
